import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface CommentReaction {
  id: string;
  comment_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

export interface CommentReactionSummary {
  emoji: string;
  count: number;
  hasReacted: boolean;
  users: string[];
}

export const COMMENT_REACTIONS = ['❤️', '👍', '🔥', '😂', '🤔', '👎', '🤮'];

export function useCommentReactions(pollId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all reactions for comments in this poll
  const { data: reactions = [], isLoading } = useQuery({
    queryKey: ['poll-comment-reactions', pollId],
    queryFn: async () => {
      if (!pollId || pollId.startsWith('feed-')) return []; // Skip mock polls

      // Get all comment IDs for this poll first
      const { data: comments } = await supabase
        .from('poll_comments')
        .select('id')
        .eq('poll_id', pollId);

      if (!comments || comments.length === 0) return [];

      const commentIds = comments.map(c => c.id);

      // Fetch reactions for those comments
      const { data: reactionsData, error } = await supabase
        .from('poll_comment_reactions')
        .select('*')
        .in('comment_id', commentIds);

      if (error) throw error;
      return (reactionsData || []) as CommentReaction[];
    },
    enabled: !!pollId && !pollId.startsWith('feed-'),
  });

  // Get reactions grouped by comment
  const getReactionsForComment = useCallback((commentId: string): CommentReactionSummary[] => {
    const commentReactions = reactions.filter(r => r.comment_id === commentId);
    
    // Group by emoji
    const emojiGroups = new Map<string, { count: number; users: string[]; hasReacted: boolean }>();
    
    commentReactions.forEach(r => {
      const existing = emojiGroups.get(r.emoji) || { count: 0, users: [], hasReacted: false };
      existing.count++;
      existing.users.push(r.user_id);
      if (r.user_id === user?.id) {
        existing.hasReacted = true;
      }
      emojiGroups.set(r.emoji, existing);
    });

    return Array.from(emojiGroups.entries()).map(([emoji, data]) => ({
      emoji,
      count: data.count,
      hasReacted: data.hasReacted,
      users: data.users,
    }));
  }, [reactions, user?.id]);

  // Toggle reaction mutation
  const toggleReactionMutation = useMutation({
    mutationFn: async ({ commentId, emoji }: { commentId: string; emoji: string }) => {
      if (!user) throw new Error('Not authenticated');

      // Check if user already reacted with this emoji
      const existing = reactions.find(
        r => r.comment_id === commentId && r.user_id === user.id && r.emoji === emoji
      );

      if (existing) {
        // Remove reaction
        const { error } = await supabase
          .from('poll_comment_reactions')
          .delete()
          .eq('id', existing.id);
        
        if (error) throw error;
        return { action: 'removed', emoji };
      } else {
        // Add reaction
        const { error } = await supabase
          .from('poll_comment_reactions')
          .insert({
            comment_id: commentId,
            user_id: user.id,
            emoji,
          });
        
        if (error) throw error;
        return { action: 'added', emoji };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['poll-comment-reactions', pollId] });
    },
  });

  const toggleReaction = useCallback((commentId: string, emoji: string) => {
    return toggleReactionMutation.mutateAsync({ commentId, emoji });
  }, [toggleReactionMutation]);

  return {
    reactions,
    isLoading,
    getReactionsForComment,
    toggleReaction,
    isToggling: toggleReactionMutation.isPending,
  };
}
